import { useState, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import { Box, CircularProgress, Alert } from '@mui/material';
import { PdfViewerToolbar } from './PdfViewerToolbar';
import { usePdfDocument } from '../../hooks/usePdfDocument';
import type { PdfViewerProps } from '../../types/pdf.types';

import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

export function PdfViewer({ documentId, fileName, onDownload }: PdfViewerProps) {
  const [numPages, setNumPages] = useState<number | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1);

  const { blobUrl, isLoading, error } = usePdfDocument(documentId);

  const handleDocumentLoadSuccess = useCallback(
    ({ numPages: pages }: { numPages: number }) => {
      setNumPages(pages);
      setCurrentPage(1);
    },
    []
  );

  const handleDownload = useCallback(() => {
    if (onDownload) {
      onDownload();
    } else if (blobUrl) {
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = fileName;
      a.click();
    }
  }, [blobUrl, fileName, onDownload]);

  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          minHeight: 400,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!blobUrl) {
    return null;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PdfViewerToolbar
        currentPage={currentPage}
        numPages={numPages}
        scale={scale}
        onPageChange={setCurrentPage}
        onScaleChange={setScale}
        onDownload={handleDownload}
        fileName={fileName}
      />

      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          display: 'flex',
          justifyContent: 'center',
          bgcolor: 'action.hover',
          p: 2,
        }}
      >
        <Document
          file={blobUrl}
          onLoadSuccess={handleDocumentLoadSuccess}
          loading={
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          }
          error={
            <Alert severity="error">Fehler beim Laden des PDFs</Alert>
          }
        >
          <Page
            pageNumber={currentPage}
            scale={scale}
            loading={
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress size={24} />
              </Box>
            }
          />
        </Document>
      </Box>
    </Box>
  );
}
